
import axios from 'axios'
import userApi from '@/api/userAPI'
import groupApi from '@/api/groupAPI'
import administrationAPI from '../../api/administrationAPI'

export const userStore = {
    // This makes your getters, mutations, and actions accessed by, eg: 'userStore/{name}' instead of mounting getters, mutations, and actions to the root namespace.
    namespaced: true,

    state: {
        isLoggedIn: 0,
        isLoggedInAdmin: 0,
        log_in_as_actor_id: 0,
        user: {
            user_id: 0,
            firstname: '',
            lastname: '',
            token: localStorage.getItem('id_token')
        },
        user_settings: {
            language: 'en',
            notifications_email_update_message: false,
            notifications_email_update_posts: false,
            editor_font_size: '14', // Default font size for Froala editor (px)
        },
        // Flat { name: 1 } map, kept for the ~100 call sites that only ask
        // "does this user hold X anywhere". Institution-blind by construction.
        permissions: {},
        // The same grants with their institution_id intact. Every permission
        // check that matters resolves against THIS — the flat map made a
        // teacher in institution A look like a teacher in institution B.
        permissions_scoped: [],
        admin_groups: {},
        profile_user_info: {},
        queriedUsers: [],
        // TODO: Should be used instead of permissions
        default_institution: null,
        institutions: [],
        institutions_settings: {},
        user_permissions: {},
        active_users: null,
        institution_teachers: null,
        institutionInfo: null
    },
    getters: {
        nonInstitutionUser: (state) => {
            if (!state.default_institution) {
                return true
            }
            return !state.default_institution.client?.paying
        },
        userIsGroupOwner: (state, getters, rootState) => (group_id) => {
            if(!state.user) return false
            if (state.user.is_admin === 1) {
                return true
            }
            const group_institution_id = (rootState.groupStore.info[group_id] && rootState.groupStore.info[group_id].parent_group) ? +rootState.groupStore.info[group_id].parent_group : 0 || 0
            const user_institution_id = state.default_institution? state.default_institution.group_id : 0
            if (
                group_institution_id &&
                user_institution_id &&
                (parseInt(group_institution_id) === parseInt(user_institution_id)) &&
                getters.userHoldsPermission('admin_groups_classes', group_institution_id)
            ) {
                return true
            }
            return (state.admin_groups[group_id] === 1)
        },
        // May the user SEE this group's teacher-side view, without being able
        // to change anything in it? True for owners — they read as well as
        // write — plus holders of `view_groups_classes`, the read-only sibling
        // of `admin_groups_classes` (Frederiksberg VUC's "se men ikke røre"-
        // rolle, 2026-08-27).
        //
        // Use this to decide whether a teacher VIEW renders. Keep using
        // userIsGroupOwner for edit affordances: it stays false for a
        // read-only holder, which is what hides every button without a sweep.
        userCanViewGroup: (state, getters, rootState) => (group_id) => {
            if (!state.user) return false
            if (getters.userIsGroupOwner(group_id)) {
                return true
            }
            // parent_group arrives as a string — parseInt both sides.
            const group_institution_id = (rootState.groupStore.info[group_id] && rootState.groupStore.info[group_id].parent_group) ? +rootState.groupStore.info[group_id].parent_group : 0
            const user_institution_id = state.default_institution ? state.default_institution.group_id : 0
            return !!(
                group_institution_id &&
                user_institution_id &&
                (parseInt(group_institution_id) === parseInt(user_institution_id)) &&
                getters.userHoldsPermission('view_groups_classes', group_institution_id)
            )
        },
        // Is the user a read-only viewer of this hold (group_viewers,
        // "læseadgang")? They see its content as a student does and may write
        // nothing, so use this to hide hand-in, composers and quiz answering.
        // The API sets the flag on GET /groups/info; a member is never one.
        userIsGroupViewer: (state, getters, rootState) => (group_id) => {
            const info = rootState.groupStore.info[group_id]
            return !!(info && info.is_viewer)
        },
        /*userIsGroupOwner: (state, getters, rootState) => (group_id) => {
            if (!state.user) return false

            if (state.user.is_admin === 1) {
                return true
            }

            const groupData = rootState.groupStore.userGroups.find((group) => +group.group_id === +group_id);

            const group_institution_id = groupData ? groupData.parent_group : 0

            return getters.userIsGroupAdmin(group_id) ||
                getters.userHasPermissionInInstitution(
                    'admin_groups_classes',
                    group_institution_id
                )
        },*/
        // Does the user hold this permission IN THE INSTITUTION THEY ARE
        // CURRENTLY IN? Pass an explicit institution_id to ask about another
        // one. Mirrors the backend's LegacyUser::hasPermission, which has
        // always matched on the (name, institution_id) pair.
        userHoldsPermission: (state) => (permission_name, institution_id) => {
            if (!state.user) return false
            if (state.user.is_admin === 1) {
                return true
            }

            const scope = typeof institution_id === 'undefined'
                ? (state.default_institution ? state.default_institution.group_id : 0)
                : institution_id

            // Institution ids arrive from the API as strings — parseInt both sides.
            return state.permissions_scoped.some((permission) =>
                permission.name === permission_name &&
                permission.institution_id === parseInt(scope)
            )
        },
        // May this user look at somebody ELSE's profile pages — their
        // assignments, quizzes and courses?
        //
        // Mirrors the backend, which gates those reads on canViewGroup: group
        // owners, `admin_groups_classes` and `view_groups_classes`. It used to
        // be a check for `profile_assignments` / `profile_quizzes` /
        // `profile_paths`, which lived in the default teacher template — those
        // permissions were dropped on 2026-08-28 when the backend collapsed to
        // canViewGroup, and every profile tab silently vanished for admins who
        // did not also hold `view_groups_classes`.
        //
        // The per-group decision still belongs to the API. This only decides
        // whether the tabs are offered at all.
        userCanViewOtherProfiles: (state, getters) => {
            return getters.userHoldsPermission('teacher')
                || getters.userHoldsPermission('admin_groups_classes')
                || getters.userHoldsPermission('view_groups_classes')
        },
        userHasPermission: (state, getters) => (permission_name) => {
            return getters.userHoldsPermission(permission_name)
        },
        userIsTeacher: (state) => {
            return state.user.is_teacher
        },
        userIsStudent: (state) => {
            return !state.user.is_teacher
        },
        userIsTrial: (state) => {
            return state.user.trial
        },
        userIsGroupAdmin: (state) => (group_id) => {
            const permissions = state.user_permissions.groups_permissions || [];

            return -1 !== permissions.findIndex((permission) =>
                +permission.group_id === +group_id && permission.is_admin
            )
        },
        userHasPermissionInInstitution: (state) => (permission_name, institution_id) => {
            const permissions = state.user_permissions.institutions_permissions || [];

            if (typeof institution_id === 'undefined') {
                institution_id = state.default_institution
                    ? state.default_institution.group_id
                    : null
            }

            return -1 !== permissions.findIndex((permission) =>
                +permission.institution_id === +institution_id && permission.name === permission_name
            )
        },
        userIsInstitutionOwner: (state, getters) => {
            if (!state.default_institution) return false
            return getters.userHasPermissionInInstitution('admin_groups_classes')
        }
    },
    actions: {
        async getInstitutionInfo({ commit }) {
            try {
                const response = await userApi.getInstitutionInfo();
                if(response) {
                    commit(
                        "SET_INSTITUTION_INFO",
                        response.data
                    );
                }

            } catch (error) {
                console.log(error);
            }
        },
        async getUserInstitutions({ commit }) {
            try {
                await userApi.getUserInstitutions()
            } catch (error) {
                console.log(error)
            }
        },
        async setUserDefaultInstitution({commit}, {institution_id}) {
            try {
                await userApi.setUserDefaultInstitution(institution_id)
            } catch (error) {
                console.log(error)
            }
        },
        /**
         * Function for creating a new user
         */
        async createUser({ commit }, info) {
            return await userApi.createUser(info)
        },
        /**
         * Attach an existing account to the institution — what createUser
         * cannot do once the email is taken by a user who was removed from it.
         */
        async reAddUser({ commit }, info) {
            return await userApi.reAddUser(info)
        },
        async setUserClientType({ commit }, {client_type, institution_id}) {
            return await userApi.setAccountType({client_type, institution_id})
        },
        async clearLoggedInUserInfo({ commit }) {

            // reset user obj and logged in
            commit('CLEAR_LOGGED_IN_USER')
            commit('SET_LOGGED_IN_USER', 0)
            return 1
        },
        async createCompanyUser({ commit, state },{ firstname, lastname, email, group_id }) {
            return await userApi.createCompanyUser({ firstname, lastname, email, group_id })
        },
        /**
         * Function for getting info about a user
         */
        async getUserInfo({ state, commit }, { user_id, force_refresh = false }) {

            if (!force_refresh && state.profile_user_info[user_id] && state.profile_user_info[user_id]['user_id']) {
                return state.profile_user_info[user_id]
            } else {

                const userInfoResponse = await userApi.getUserInfo(user_id)

                let user_data = userInfoResponse.data
                commit('SET_PROFILE_USER_DATA', {
                    user_id: user_id,
                    user_data: user_data
                })

                return user_data
            }
        },
        async getInstitutionTeachers({ state, commit }) {
            const response = await userApi.getInstitutionTeachers()
            commit('SET_INSTITUTION_USERS', response.data)
        },
        /**
         * Function for pulling all info about a user when logging in from token
         */
        async pullLoggedInUserInfo({ commit, dispatch, state }, token) {

            const response = await userApi.pullLoggedInUserInfo(token)
            const user_data = response.data
            if (user_data && user_data.user && parseInt(user_data.user.user_id)) {
                // token can change after some time. Ask AP for clarification if needed
                const token = localStorage.getItem('id_token')
                if (token !== user_data['token']) {
                    localStorage.setItem('id_token', user_data['token'])
                    axios.defaults.headers.common['Authorization'] = 'Bearer ' + user_data['token']
                }
                commit('UPDATE_LOGGED_IN_USER', user_data.user)
                if (user_data.log_in_as_user_id) {
                    commit('UPDATE_LOG_IN_AS_USER', { user_id: user_data.log_in_as_user_id })
                }

                commit('UPDATE_LOGGED_IN_USER_PERMISSIONS', user_data.permissions)
                commit('UPDATE_LOGGED_IN_USER_ADMIN_GROUPS', user_data.admin_groups)
                if (user_data.user_settings && user_data.user_settings.length) {
                    for (let setting of user_data.user_settings) {
                        commit('UPDATE_USER_SETTING', {
                            setting: setting.type,
                            value: setting.value
                        })
                    }
                }

                commit('SET_USER_DEFAULT_INSTITUTION', user_data.default_institution)
                commit('SET_USER_INSTITUTIONS', user_data.institutions)
                commit('SET_USER_INSTITUTIONS_SETTINGS', user_data.institutions_settings)

                commit('SET_LOGGED_IN_USER', 1)

                await dispatch('getUserPermissions')
            }

            return response
        },
        /**
         * Function for saving a user
         */
        async removeUser({ commit }, user_id) {
            return await userApi.removeUser(user_id)
        },
        /**
         * Function for saving a user
         */
        async saveUser({ commit, state }, { user_id, info }) {
          await userApi.saveUser({
              user_id,
              info
          })

          commit('UPDATE_LOGGED_IN_USER', {
            ...state.user,
            ...info
          })
          commit('SET_PROFILE_USER_DATA', {
            user_id,
            user_data: {
              ...(state.profile_user_info[user_id] || {}),
              ...info
            }
          })
        },
        /**
         * @desc Search users
         * @param {Object} vx - Vuex specific functions
         * @param {Object} data - Search user data
         * @return {Promise} response
         */
        async searchUsers(vx, data) {
            const { commit } = vx
            const {
                group_id,
                institution_id,
                only_admins = false,
                only_students = false,
                txt,
            } = data

            const response = await userApi.searchUsers({
                group_id,
                institution_id,
                only_admins,
                only_students,
                txt,
            })

            commit('SET_QUERIED_USERS', {
                users: response.data,
            })

            return response
        },
        async setLoggedInUserInfo({ commit }, user_data) {
            commit('UPDATE_LOGGED_IN_USER', user_data.user)
            commit('UPDATE_LOGGED_IN_USER_PERMISSIONS', user_data.permissions)
            commit('UPDATE_LOGGED_IN_USER_ADMIN_GROUPS', user_data.admin_groups)
            // The login payload carries it (UserAdmin::getTokenUserData) and
            // permission checks are now scoped by it, so it has to land here
            // and not only in pullLoggedInUserInfo.
            if (user_data.default_institution) {
                commit('SET_USER_DEFAULT_INSTITUTION', user_data.default_institution)
            }
            return 1
        },
        async setUserAdminLoggedIn({ commit }, flag) {
            commit('SET_LOGGED_IN_ADMIN', flag)
            return 1
        },
        async setUserLoggedIn({ commit }, flag) {
            commit('SET_LOGGED_IN_USER', flag)
            return 1
        },
        /**
         * Function for saving a user email
         */
        async setUserEmail({ commit }, { user_id, email }) {
            return await userApi.saveUserEmail({
                user_id,
                email
            })
        },
        /**
         * Function for saving a user language
         */
        async setUserLanguage({ commit }, { user_id, language }) {
            const response = userApi.saveUserLanguage({
                user_id,
                language
            })
            commit('UPDATE_USER_SETTING', {
                setting: 'language',
                value: language
            })
            return await response
        },
        async setUserSetting({ commit, state }, { user_id, setting, value }) {
            // Commit before the round-trip and roll back if it fails: the UI
            // reads these settings synchronously right after dispatching, and
            // `show_ui_toasts` in particular governs the very toast that
            // confirms the save.
            const previous = state.user_settings[setting]
            commit('UPDATE_USER_SETTING', {
                setting: setting,
                value: value
            })

            try {
                return await userApi.saveUserSetting({
                    user_id: user_id,
                    setting: setting,
                    value: value
                })
            } catch (error) {
                commit('UPDATE_USER_SETTING', {
                    setting: setting,
                    value: previous
                })
                throw error
            }
        },
        async setProfileUserData({ commit }, { user_id, user_data }) {
            commit('SET_PROFILE_USER_DATA', {
                user_id: user_id,
                user_data: user_data
            })
            return 1
        },
        /**
         * Function for saving a user language
         */
        async setUserPassword({ commit }, { password_old, password_new }) {
            return await userApi.setUserPassword({
                password_old: password_old,
                password_new: password_new
            })
        },
        async setUserProfilePicture({ commit }, { media_data }) {
            return await userApi.setUserProfilePicture({
                media_data: media_data
            })
        },
        async getUserPermissions({ commit }) {
            const response = await userApi.getPermissions()

            commit('SET_USER_PERMISSIONS', response.data);
        }
    },
    mutations: {
        CLEAR_LOGGED_IN_USER: (state) => {
            state.user = {}
            state.permissions = []
            state.permissions_scoped = []
            state.admin_groups = []
        },
        SET_LOGGED_IN_ADMIN: (state, flag) => {
            state.isLoggedInAdmin = flag
        },
        SET_LOGGED_IN_USER: (state, flag) => {
            state.isLoggedIn = flag
        },

        SET_PROFILE_USER_DATA: (state, { user_id, user_data }) => {
            state.profile_user_info[user_id] = user_data
        },
        SET_QUERIED_USERS: (state, data) => {
            const { users } = data

            state.queriedUsers = users
        },
        UPDATE_LOGGED_IN_USER: (state, user) => {
            state.user = user
        },
        UPDATE_LOGGED_IN_USER_PERMISSIONS: (state, permissions) => {
            // create object to speed up checks and make it easier
            if(!permissions) return 0

            // Keep the institution_id. Flattening it away is what let a
            // teacher in institution A see teacher affordances in institution
            // B — the backend's hasPermission has always matched on the pair,
            // so the UI offered buttons that 403'd.
            state.permissions_scoped = permissions.map((permission) => ({
                name: permission.name,
                institution_id: parseInt(permission.institution_id),
            }))

            let permObj = {}
            let length = permissions.length
            for (let i = 0; i < length; i++) {
                permObj[permissions[i]['name']] = 1
            }
            state.permissions = permObj
        },
        UPDATE_LOGGED_IN_USER_ADMIN_GROUPS: (state, groups) => {
            // create object to speed up checks and make it easier
            if(!groups) return 0
            let groupObj = {}
            let length = groups.length
            for (let i = 0; i < length; i++) {
                groupObj[groups[i]] = 1
            }
            state.admin_groups = groupObj
        },
        UPDATE_LOG_IN_AS_USER: (state, { user_id }) => {
            state.log_in_as_actor_id = user_id
        },
        UPDATE_USER_SETTING: (state, { setting, value }) => {
            state.user_settings[setting] = value
        },
        SET_USER_PERMISSIONS: (state, permissions) => {
            state.user_permissions = permissions
        },
        SET_USER_DEFAULT_INSTITUTION: (state, institution) => {
            state.default_institution = institution
        },
        SET_USER_INSTITUTIONS: (state, institutions) => {
            state.institutions = institutions
        },
        SET_USER_INSTITUTIONS_SETTINGS: (state, institutions_settings) => {
            state.institutions_settings = institutions_settings
        },
        SET_ACTIVE_USERS: (state, data) => {
            state.active_users = data
        },
        SET_INSTITUTION_USERS: (state, data) => {
            state.institution_teachers = data
        },
        SET_INSTITUTION_INFO: (state, data) => {
            state.institutionInfo = data
        }
    }
}
